import React, { useMemo } from 'react';
import styled from 'styled-components';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

interface PasswordValidation {
  isValid: boolean;
  strength: 'weak' | 'fair' | 'good' | 'strong';
  strengthScore: number;
  checks: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
    noCommonPatterns: boolean;
  };
}

const COMMON_WEAK_PATTERNS = [
  'password', 'password1', 'password123', '123456', '12345678', '123456789',
  'qwerty', 'abc123', 'letmein', 'welcome', 'admin', 'login', 'master',
];

function validatePassword(password: string): PasswordValidation {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password),
    noCommonPatterns: !COMMON_WEAK_PATTERNS.some(pattern => 
      password.toLowerCase().includes(pattern)
    ),
  };

  let strengthScore = 0;
  if (checks.minLength) strengthScore += 1;
  if (password.length >= 12) strengthScore += 1;
  if (checks.hasUppercase) strengthScore += 1;
  if (checks.hasLowercase) strengthScore += 1;
  if (checks.hasNumber) strengthScore += 1;
  if (checks.hasSpecial) strengthScore += 1;
  if (!checks.noCommonPatterns) strengthScore = Math.max(0, strengthScore - 2);

  let strength: 'weak' | 'fair' | 'good' | 'strong';
  if (strengthScore <= 2) strength = 'weak';
  else if (strengthScore <= 4) strength = 'fair';
  else if (strengthScore <= 5) strength = 'good';
  else strength = 'strong';

  const isValid = Object.values(checks).every(Boolean);

  return { isValid, strength, strengthScore, checks };
}

const Container = styled.div`
  margin-top: 8px;
`;

const StrengthBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`;

const StrengthSegment = styled.div<{ $active: boolean; $color: string }>`
  height: 4px;
  flex: 1;
  border-radius: 2px;
  background-color: ${props => props.$active ? props.$color : 'var(--color-line)'};
  transition: background-color 0.2s ease;
`;

const StrengthLabel = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
`;

const RequirementItem = styled.li<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${props => props.$met ? 'var(--color-success)' : 'var(--color-text-tertiary)'};
  margin-bottom: 4px;
  
  &::before {
    content: ${props => props.$met ? '"✓"' : '"○"'};
    font-size: 10px;
    color: ${props => props.$met ? 'var(--color-success)' : 'var(--color-text-secondary)'};
  }
`;

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ 
  password, 
  showRequirements = true 
}) => {
  const validation = useMemo(() => validatePassword(password), [password]);

  if (!password) {
    return null;
  }

  const strengthColors = {
    weak: 'var(--color-error)',
    fair: 'var(--color-report)',
    good: 'var(--color-job)',
    strong: 'var(--color-success)',
  };

  const strengthLabels = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  const color = strengthColors[validation.strength];
  const segmentsActive = {
    weak: 1,
    fair: 2,
    good: 3,
    strong: 4,
  }[validation.strength];

  return (
    <Container>
      <StrengthBar>
        {[1, 2, 3, 4].map(segment => (
          <StrengthSegment
            key={segment}
            $active={segment <= segmentsActive}
            $color={color}
          />
        ))}
      </StrengthBar>
      <StrengthLabel $color={color}>
        Password strength: {strengthLabels[validation.strength]}
      </StrengthLabel>
      
      {showRequirements && (
        <RequirementsList>
          <RequirementItem $met={validation.checks.minLength}>
            At least 8 characters
          </RequirementItem>
          <RequirementItem $met={validation.checks.hasUppercase}>
            One uppercase letter (A-Z)
          </RequirementItem>
          <RequirementItem $met={validation.checks.hasLowercase}>
            One lowercase letter (a-z)
          </RequirementItem>
          <RequirementItem $met={validation.checks.hasNumber}>
            One number (0-9)
          </RequirementItem>
          <RequirementItem $met={validation.checks.hasSpecial}>
            One special character (!@#$%^&*)
          </RequirementItem>
          <RequirementItem $met={validation.checks.noCommonPatterns}>
            No common patterns
          </RequirementItem>
        </RequirementsList>
      )}
    </Container>
  );
};

export default PasswordStrengthIndicator;
